import { useMemo, type ComponentType } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Boxes,
  ChartColumn,
  CircleHelp,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Factory,
  History,
  House,
  LayoutGrid,
  MonitorSmartphone,
  PackagePlus,
  Settings,
  ShoppingBag,
  Sparkles,
  Ticket,
  UserRound,
  Users,
  Wallet,
} from 'lucide-react';
import type { FeatureKey, OrderCountsDTO, PermissionKey } from '@dezza/shared';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';

export type NavIcon = ComponentType<{
  className?: string;
  strokeWidth?: number;
  'aria-hidden'?: boolean | 'true' | 'false';
}>;

export interface NavItem {
  key: string;
  to: string;
  label: string;
  icon: NavIcon;
  /** Visível para quem tem QUALQUER uma destas permissões (sem = todos). */
  perm?: PermissionKey[];
  /** Recurso do plano: sem ele, o item aparece com cadeado e abre o upgrade. */
  feature?: FeatureKey;
  /** Selo "Novo" (lima). */
  isNew?: boolean;
  /** Contador ao vivo ao lado do item. */
  badge?: 'orders';
  /** Destaque visual (Vender). */
  emphasis?: boolean;
  /** Sinônimos para a paleta de comandos. */
  keywords?: string[];
  /** Uma frase sobre a tela (estado "Em breve" e paleta). */
  description?: string;
}

export interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
}

/**
 * Arquitetura de informação do painel (contrato de rotas combinado entre os agentes).
 * Ordem, rótulos, permissões e recursos de plano do menu lateral, da folha "Mais" e da paleta.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    key: 'principal',
    label: 'Principal',
    items: [
      {
        key: 'inicio',
        to: '/inicio',
        label: 'Início',
        icon: House,
        keywords: ['dashboard', 'resumo', 'painel'],
      },
      {
        key: 'vender',
        to: '/vender',
        label: 'Vender',
        icon: ShoppingBag,
        perm: ['sales.create'],
        emphasis: true,
        keywords: ['pdv', 'caixa', 'venda', 'balcão'],
      },
      {
        key: 'pedidos',
        to: '/pedidos',
        label: 'Pedidos',
        icon: ClipboardList,
        perm: ['orders.manage', 'sales.create'],
        badge: 'orders',
        keywords: ['encomendas', 'condicional', 'entregas'],
      },
      {
        key: 'clientes',
        to: '/clientes',
        label: 'Clientes',
        icon: UserRound,
        keywords: ['fiado', 'contatos', 'aniversários'],
      },
    ],
  },
  {
    key: 'produtos',
    label: 'Produtos e estoque',
    items: [
      {
        key: 'produtos',
        to: '/produtos',
        label: 'Produtos',
        icon: LayoutGrid,
        keywords: ['itens', 'mercadorias', 'categorias', 'variações'],
      },
      {
        key: 'estoque',
        to: '/estoque',
        label: 'Estoque',
        icon: Boxes,
        perm: ['stock.manage', 'products.manage'],
        feature: 'stock',
        keywords: ['reposição', 'cobertura', 'esgotados'],
        description: 'Cobertura de estoque, reposição sugerida e o que está parado.',
      },
      {
        key: 'compras',
        to: '/estoque/compras',
        label: 'Compras',
        icon: PackagePlus,
        perm: ['purchases.manage'],
        feature: 'purchases',
        keywords: ['nota fiscal', 'nf-e', 'entrada de mercadoria'],
        description: 'Registre compras, importe a NF‑e e receba a mercadoria no estoque.',
      },
      {
        key: 'fornecedores',
        to: '/estoque/fornecedores',
        label: 'Fornecedores',
        icon: Factory,
        perm: ['purchases.manage'],
        feature: 'purchases',
        keywords: ['fábrica', 'atacado'],
        description: 'Contatos, prazos e histórico de compras de cada fornecedor.',
      },
      {
        key: 'inventario',
        to: '/estoque/inventarios',
        label: 'Inventário',
        icon: ClipboardCheck,
        perm: ['inventory.manage'],
        feature: 'inventory',
        keywords: ['contagem', 'balanço'],
        description: 'Conte o estoque por grade e aplique os ajustes de uma vez.',
      },
    ],
  },
  {
    key: 'loja',
    label: 'Loja online',
    items: [
      {
        key: 'catalogo',
        to: '/catalogo',
        label: 'Catálogo',
        icon: MonitorSmartphone,
        perm: ['catalog.manage'],
        keywords: ['loja online', 'vitrine', 'site'],
      },
      {
        key: 'cupons',
        to: '/cupons',
        label: 'Cupons',
        icon: Ticket,
        perm: ['coupons.manage'],
        feature: 'coupons',
        keywords: ['desconto', 'promoção'],
      },
      {
        key: 'cobrancas',
        to: '/pedidos/cobrancas',
        label: 'Cobranças online',
        icon: CreditCard,
        perm: ['payments.manage'],
        feature: 'onlinePayments',
        keywords: ['pix', 'cartão', 'boleto', 'mercado pago', 'asaas'],
        description: 'Pix, cartão e boleto pagos pelo site, com a situação de cada cobrança.',
      },
    ],
  },
  {
    key: 'gestao',
    label: 'Gestão',
    items: [
      {
        key: 'financas',
        to: '/financas',
        label: 'Finanças',
        icon: Wallet,
        perm: ['finance.view', 'cash.operate'],
        keywords: ['caixa', 'despesas', 'contas'],
      },
      {
        key: 'inteligencia',
        to: '/inteligencia',
        label: 'Inteligência',
        icon: Sparkles,
        perm: ['intelligence.view'],
        feature: 'intelligence',
        isNew: true,
        keywords: ['recomendações', 'matriz', 'previsão'],
        description: 'Matriz de produtos, previsão de estoque e recomendações para vender mais.',
      },
      {
        key: 'estatisticas',
        to: '/estatisticas',
        label: 'Estatísticas',
        icon: ChartColumn,
        perm: ['reports.view'],
        keywords: ['relatórios', 'gráficos'],
      },
      {
        key: 'historico',
        to: '/historico',
        label: 'Histórico',
        icon: History,
        perm: ['sales.create', 'sales.view_all'],
        keywords: ['vendas', 'recibos'],
      },
    ],
  },
  {
    key: 'conta',
    label: 'Conta',
    items: [
      {
        key: 'equipe',
        to: '/usuarios',
        label: 'Equipe',
        icon: Users,
        perm: ['users.manage'],
        keywords: ['usuários', 'vendedores', 'permissões'],
      },
      {
        key: 'configuracoes',
        to: '/configuracoes',
        label: 'Configurações',
        icon: Settings,
        keywords: ['ajustes', 'preferências'],
      },
      {
        key: 'ajuda',
        to: '/ajuda',
        label: 'Ajuda',
        icon: CircleHelp,
        keywords: ['suporte', 'dúvidas', 'whatsapp'],
      },
    ],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

export interface VisibleNavItem extends NavItem {
  locked: boolean;
}
export interface VisibleNavGroup extends Omit<NavGroup, 'items'> {
  items: VisibleNavItem[];
}

/** Menu filtrado pelas permissões do usuário, com cadeado onde o plano não inclui o recurso. */
export function useNav(): VisibleNavGroup[] {
  const me = useMe();
  const perms = me.membership.permissions;
  const features = me.entitlements.features;
  return useMemo(
    () =>
      NAV_GROUPS.map((g) => ({
        ...g,
        items: g.items
          .filter((i) => !i.perm || i.perm.some((p) => perms.includes(p)))
          .map((i) => ({ ...i, locked: i.feature ? !features[i.feature] : false })),
      })).filter((g) => g.items.length > 0),
    [perms, features],
  );
}

const clean = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

/** Item do menu que corresponde ao endereço (o prefixo mais longo vence: /pedidos/cobrancas não acende Pedidos). */
export function matchNavItem(pathname: string, items: NavItem[] = ALL_NAV_ITEMS): NavItem | null {
  const path = clean(pathname);
  let best: NavItem | null = null;
  for (const item of items) {
    if (path === item.to || path.startsWith(`${item.to}/`)) {
      if (!best || item.to.length > best.to.length) best = item;
    }
  }
  return best;
}

/** Telas de primeiro nível (mostram a barra de abas do celular). O PDV tem a própria barra. */
export function isTopLevelPath(pathname: string): boolean {
  const path = clean(pathname);
  if (path === '/vender') return false;
  if (path === '/notificacoes') return true;
  return ALL_NAV_ITEMS.some((i) => i.to === path);
}

/** Contadores de pedidos (mesma chave do Pedidos: o tempo real invalida e o número muda sozinho). */
export function useOrdersBadge(): number {
  const me = useMe();
  const perms = me.membership.permissions;
  const enabled = perms.includes('orders.manage') || perms.includes('sales.create');
  const counts = useQuery({
    queryKey: ['orders', 'counts'],
    queryFn: () => api.get<OrderCountsDTO & { conditional: number }>('/orders/counts'),
    refetchInterval: 60_000,
    enabled,
  });
  return enabled ? (counts.data?.active ?? 0) + (counts.data?.conditional ?? 0) : 0;
}

/** Telas que não estão no menu mas valem na busca (Ctrl + K). */
export interface ExtraPage {
  key: string;
  to: string;
  label: string;
  hint: string;
  perm?: PermissionKey[];
  keywords?: string[];
}

export const EXTRA_PAGES: ExtraPage[] = [
  {
    key: 'notificacoes',
    to: '/notificacoes',
    label: 'Notificações',
    hint: 'Avisos',
    keywords: ['avisos', 'alertas'],
  },
  {
    key: 'caixa',
    to: '/financas/caixa',
    label: 'Caixa',
    hint: 'Finanças',
    perm: ['cash.operate', 'finance.view'],
    keywords: ['abrir caixa', 'fechar caixa', 'sangria'],
  },
  {
    key: 'lancamentos',
    to: '/financas/lancamentos',
    label: 'Lançamentos',
    hint: 'Finanças',
    perm: ['finance.view'],
    keywords: ['contas a pagar', 'despesas', 'receitas'],
  },
  { key: 'fin-contas', to: '/financas/contas', label: 'Contas e saldos', hint: 'Finanças', perm: ['finance.view'], keywords: ['banco', 'saldo', 'carteira', 'nubank'] },
  { key: 'fin-transferencias', to: '/financas/transferencias', label: 'Transferências entre contas', hint: 'Finanças', perm: ['finance.view'], keywords: ['transferir', 'sangria'] },
  { key: 'fin-dre', to: '/financas/dre', label: 'DRE: resultado do mês', hint: 'Finanças', perm: ['finance.view'], keywords: ['lucro', 'resultado', 'demonstrativo', 'cmv', 'ponto de equilíbrio'] },
  { key: 'fin-fluxo', to: '/financas/fluxo', label: 'Fluxo de caixa', hint: 'Finanças', perm: ['finance.view'], keywords: ['previsão', 'projeção', 'saldo futuro'] },
  { key: 'fin-recorrentes', to: '/financas/recorrentes', label: 'Despesas fixas', hint: 'Finanças', perm: ['finance.view'], keywords: ['aluguel', 'salário', 'recorrente', 'mensal'] },
  { key: 'fin-conciliacao', to: '/financas/conciliacao', label: 'Conciliação bancária', hint: 'Finanças', perm: ['finance.view'], keywords: ['extrato', 'ofx', 'csv', 'banco'] },
  { key: 'fin-metas', to: '/financas/metas', label: 'Metas do mês', hint: 'Finanças', perm: ['finance.view'], keywords: ['objetivo', 'meta de vendas'] },
  { key: 'fin-categorias', to: '/financas/categorias', label: 'Categorias financeiras', hint: 'Finanças', perm: ['finance.view'], keywords: ['plano de contas', 'dre'] },
  { key: 'int-matriz', to: '/inteligencia?aba=matriz', label: 'Matriz de produtos', hint: 'Inteligência', perm: ['intelligence.view'], keywords: ['estrela', 'encalhe', 'margem', 'mais vendidos'] },
  { key: 'int-ranking', to: '/inteligencia?aba=ranking', label: 'Ranking de lucro', hint: 'Inteligência', perm: ['intelligence.view'], keywords: ['lucro por produto'] },
  { key: 'int-juntos', to: '/inteligencia?aba=juntos', label: 'Compram juntos', hint: 'Inteligência', perm: ['intelligence.view'], keywords: ['complete o look', 'combinações', 'kit'] },
  { key: 'int-estoque', to: '/inteligencia?aba=estoque', label: 'Previsão de estoque e reposição', hint: 'Inteligência', perm: ['intelligence.view'], keywords: ['acaba em', 'repor', 'capital parado', 'ruptura'] },
  { key: 'int-grade', to: '/inteligencia?aba=grade', label: 'Grade ideal por tamanho e cor', hint: 'Inteligência', perm: ['intelligence.view'], keywords: ['tamanho', 'proporção', 'P M G'] },
  {
    key: 'categorias',
    to: '/produtos/categorias',
    label: 'Categorias de produtos',
    hint: 'Produtos',
    perm: ['products.manage'],
  },
  {
    key: 'loja',
    to: '/configuracoes/loja',
    label: 'Dados da loja',
    hint: 'Configurações',
    perm: ['settings.manage'],
    keywords: ['logo', 'endereço', 'cnpj'],
  },
  {
    key: 'pagamentos',
    to: '/configuracoes/pagamentos',
    label: 'Formas de pagamento e Pix',
    hint: 'Configurações',
    perm: ['settings.manage'],
    keywords: ['pix', 'taxas', 'cartão'],
  },
  {
    key: 'entrega',
    to: '/configuracoes/frete',
    label: 'Frete e entrega',
    hint: 'Configurações',
    perm: ['settings.manage'],
    keywords: ['frete', 'retirada', 'correios', 'motoboy', 'frete grátis', 'regras de frete', 'cep'],
  },
  {
    key: 'pagamentos-online',
    to: '/configuracoes/pagamentos-online',
    label: 'Pagamento online',
    hint: 'Configurações',
    perm: ['payments.manage'],
    keywords: ['mercado pago', 'asaas', 'pix no site', 'cartão no site', 'boleto'],
  },
  {
    key: 'precos',
    to: '/configuracoes/precos',
    label: 'Parâmetros de preço',
    hint: 'Configurações',
    perm: ['settings.manage'],
    keywords: ['margem', 'markup', 'impostos', 'calculadora de preço', 'precificação'],
  },
  {
    key: 'recibo',
    to: '/configuracoes/recibo',
    label: 'Recibo',
    hint: 'Configurações',
    perm: ['settings.manage'],
    keywords: ['impressora', 'cupom'],
  },
  {
    key: 'seguranca',
    to: '/configuracoes/seguranca',
    label: 'Minha conta e segurança',
    hint: 'Conta',
    keywords: ['senha', 'perfil', 'duas etapas'],
  },
  {
    key: 'plano',
    to: '/configuracoes/plano',
    label: 'Meu plano',
    hint: 'Conta',
    perm: ['billing.manage'],
    keywords: ['assinatura', 'fatura', 'upgrade'],
  },
];
